import { Tooltip } from '@systemeio/ui'
import { ArrowLeftIcon, ChevronDownIcon } from '@systemeio/ui/icons'
import {
  MenuItemType,
  SingleItemDropdownInterface,
  SingleItemHrefInterface,
} from 'shared/components/header/types/menu-interface'
import { checkNestedIsActive } from 'shared/components/header/utils/check-nested-is-active'
import { checkNestedRendered } from 'shared/components/header/utils/check-nested-rendered'
import { isFlatMenuItem } from 'shared/components/header/utils/is-flat-menu-item'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { twMerge } from 'tailwind-merge'

const DesktopMenuItem = ({ item }: { item: MenuItemType }) =>
  isFlatMenuItem(item) ? <TopMenuItemFlat {...item} /> : <TopMenuDropdown {...item} />

export default DesktopMenuItem

function TopMenuItemFlat(item: SingleItemHrefInterface) {
  if (!item.shouldRender) return null
  const isActive = item.isActive(item.href)

  return (
    <div className="group relative flex h-full items-center justify-center transition-all">
      <LinkWithoutPrefetch
        className={twMerge(
          'hover:text-background-secondary hover:border-brand-primary focus-visible:border-brand-primary focus-visible:text-background-secondary flex h-full items-center border-b-4 text-sm font-bold tracking-wide outline-hidden transition-all delay-100',
          isActive
            ? 'border-brand-primary text-background-secondary'
            : 'text-background-tertiary border-transparent',
        )}
        href={item.href}
        target={item.target}
      >
        {item.name}
      </LinkWithoutPrefetch>
    </div>
  )
}

function TopMenuDropdown(item: SingleItemDropdownInterface) {
  const isActive = checkNestedIsActive(item)
  const dropdownItems = item.dropdownItems
  if (!checkNestedRendered(item.dropdownItems)) return null

  return (
    <Tooltip
      anchor={{
        gap: -5,
      }}
      stayOnHover={true}
      mode={'hover'}
      popoverClassName={'p-0 shadow-lg rounded-lg'}
      wrapperClassName={'h-[100%]'}
      label={<TopMenuDropdownPanel dropdownItems={dropdownItems} />}
    >
      {show => (
        <button
          className={`main-transition-colors group-hover:text-background-secondary inline-flex h-full items-center border-b-4 text-sm font-bold tracking-wide delay-100 ${
            isActive || show
              ? 'border-brand-primary text-background-secondary'
              : 'text-background-tertiary border-transparent'
          } focus:outline-hidden`}
        >
          {item.name}
          <ChevronDownIcon className="ml-2" />
        </button>
      )}
    </Tooltip>
  )
}

function TopSubMenu(item: SingleItemDropdownInterface) {
  const isActive = checkNestedIsActive(item)
  const dropdownItems = item.dropdownItems
  if (!checkNestedRendered(item.dropdownItems)) return null
  return (
    <li>
      <Tooltip
        stayOnHover={true}
        mode={'hover'}
        popoverClassName={'p-0 rounded-lg shadow-lg z-50'}
        wrapperClassName={'h-[100%] w-[100%]'}
        className={'min-w-[100%]'}
        label={<TopMenuDropdownPanel dropdownItems={dropdownItems} />}
        anchor={{
          to: 'right',
        }}
      >
        {show => (
          <button className={getSubItemClass(isActive || show)}>
            {item.name}
            <ArrowLeftIcon
              className={`group-hover:fill-brand-primary main-transition-colors rotate-180 ${
                show || isActive ? 'fill-brand-primary' : 'fill-text-tertiary'
              }`}
            />
          </button>
        )}
      </Tooltip>
    </li>
  )
}

function TopMenuDropdownPanel({
  dropdownItems,
}: Pick<SingleItemDropdownInterface, 'dropdownItems'>) {
  return (
    <ul className="text-text-tertiary flex flex-col whitespace-nowrap py-4 text-sm">
      {dropdownItems.map(item => {
        if (isFlatMenuItem(item)) {
          return <TopMenuSubItem key={item.name} {...item} />
        } else {
          return <TopSubMenu key={item.name} {...item} />
        }
      })}
    </ul>
  )
}

export function TopMenuSubItem(item: SingleItemHrefInterface) {
  if (!item.shouldRender) return null
  const isActive = item.isActive(item.href)
  return (
    <li>
      <LinkWithoutPrefetch
        data-test-id={item.dataTestId}
        className={getSubItemClass(isActive)}
        href={item.href}
      >
        {item.name}
      </LinkWithoutPrefetch>
    </li>
  )
}

const getSubItemClass = (isActive: boolean) =>
  `flex main-transition-colors ${
    isActive ? 'text-brand-primary' : 'text-text-tertiary'
  } px-7 py-1 hover:text-brand-primary focus:outline-hidden focus-visible:text-brand-primary w-full items-center justify-between group`
